import {
  Avatar,
  Box,
  BoxList,
  Button,
  Icon,
  Spinner,
  Text,
} from '@factorialco/deprecated-design-system'
import { compact } from 'lodash'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import { useEmployeeJobTitle } from 'modules/documents/hooks/referenceContracts/useEmployeeJobTitle'
import employees from 'modules/employees/store/collections/employees'

interface Props {
  additionalSigners: string[]
  removeRecipient: () => void
  removeSigner: (accessId: string) => void
  showRecipient?: boolean
}

const SignersList = ({
  additionalSigners,
  removeRecipient,
  removeSigner,
  showRecipient = true,
}: Props) => {
  const { signers, employeesAsSigners } = useUnsafeMemo(() => {
    const accesses = compact(additionalSigners.map((id) => users.get(id)))
    const accessesAsSigners = compact(
      additionalSigners.map((accesId) => employees.find({ access_id: accesId }))
    )

    return { signers: accesses, employeesAsSigners: accessesAsSigners }
  }, [additionalSigners])

  const { employeeJobs, loading } = useEmployeeJobTitle({
    employeeIds: employeesAsSigners.map((employee) => employee.id),
  })

  const getJobTitle = (signer: User) => {
    return employeeJobs.find((job) => job.employee.access.id === signer.id)?.jobTitle ?? ''
  }

  if (loading) {
    return (
      <Box alignItems="center" justifyContent="center" padding="s12">
        <Spinner />
      </Box>
    )
  }

  return (
    <BoxList>
      {showRecipient && (
        <Box
          flexDirection="row"
          padding="s12"
          gap="s8"
          justifyContent="spaceBetween"
          alignItems="center"
        >
          <Box gap="s8" flexDirection="row" alignItems="center">
            <Box padding="s4" background="grey200" borderRadius={{ all: 'abs012' }}>
              <Icon.Large icon="user" />
            </Box>
            <Text.Normal weight="semibold">{i18n.t('split_documents.form.recipient')}</Text.Normal>
          </Box>
          <Button type="secondary" iconName="cross" onClick={removeRecipient} />
        </Box>
      )}
      {signers.map((signer, index) => (
        <Box
          key={signer.id}
          flexDirection="row"
          padding="s12"
          gap="s8"
          alignItems="center"
          justifyContent="spaceBetween"
          border={
            index !== 0
              ? {
                  top: {
                    width: 's1',
                    style: 'solid',
                    color: 'grey400',
                  },
                }
              : {}
          }
        >
          <Box gap="s8" flexDirection="row" alignItems="center">
            <Avatar
              label={getFullDisplayName({
                firstName: signer.attributes.first_name,
                lastName: signer.attributes.last_name,
              })}
              url={buildUrl(signer.avatar)}
              size="s40"
            />
            <Box>
              <Text.Normal weight="semibold">
                {getFullDisplayName({
                  firstName: signer.attributes.first_name,
                  lastName: signer.attributes.last_name,
                })}
              </Text.Normal>
              <Text.Normal size="100">{getJobTitle(signer)}</Text.Normal>
            </Box>
          </Box>
          <Button type="secondary" iconName="cross" onClick={() => removeSigner(signer.id)} />
        </Box>
      ))}
    </BoxList>
  )
}

export default SignersList
